// Custom metadata for role context
interface TUserMetadata {
  has_super_admin_role?: boolean;
  has_b2b_account?: boolean;
  has_tenant_admin_role?: boolean;
  app_language?: string;
  tenantId?: string;
}

// Combined type with proper overrides - avoiding strict index signature conflicts
type TPendoCustomMetadata = TUserMetadata & Partial<IPendoContext> & Record<string, any>;

interface IPendoVisitorData extends TPendoCustomMetadata {
  id: string;
  email?: string;
  full_name?: string;
  role?: string;
}

interface IPendoAccountData {
  id: string;
  name?: string;
}

// Pendo type declarations
declare global {
  interface Window {
    pendo: {
      initialize: (options: { account?: IPendoAccountData; visitor: IPendoVisitorData }) => void;
      updateOptions?: (options: { account?: IPendoAccountData; visitor: IPendoVisitorData }) => void;
      track?: (event: string, metadata?: Record<string, any>) => void;
      [key: string]: any; // Allow dynamic properties
    };
  }
}

export default defineNuxtPlugin(async () => {
  const runtimeConfig = useRuntimeConfig();
  const apiKey = runtimeConfig.public.pendoApiKey || "";

  if (!apiKey) {
    console.warn("Pendo API key is not configured");
    return {
      provide: { updatePendoContext: async () => {} },
    };
  }

  const theme = useTheming();
  const user = useUser();

  const initializePendo = async () => {
    try {
      const { getPendoMetadata } = usePendo();
      const metadata = await getPendoMetadata();
      window.pendo.initialize({
        account: {
          id: theme.appName,
          name: theme.siteName.value,
        },
        visitor: metadata as IPendoVisitorData,
      });
      console.log("Pendo is successfully initialized");
    } catch (error) {
      console.warn("Failed to initialize Pendo with context:", error);
      // Fallback to basic initialization
      window.pendo.initialize({
        account: {
          id: theme.appName,
          name: theme.siteName.value,
        },
        visitor: {
          id: user.value?.atlitId || "",
          email: user.value?.email || "",
          full_name: user.value?.fullName || "",
          has_super_admin_role: Boolean(user.value?.hasSuperAdminRole),
          has_b2b_account: Boolean(user.value?.hasB2BAccount),
          has_tenant_admin_role: Boolean(user.value?.hasTenantAdminRole),
          tenantId: user.value?.tenantId || "",
        },
      });
    }
  };

  const updatePendoContext = async (): Promise<void> => {
    if (window.pendo) {
      try {
        if (window.pendo.updateOptions) {
          const { getPendoMetadata } = usePendo();
          const metadata = await getPendoMetadata();
          window.pendo.updateOptions({
            account: {
              id: theme.appName,
              name: theme.siteName.value,
            },
            visitor: metadata,
          });
        } else {
          // Re-initialize if updateOptions is not available
          await initializePendo();
        }
      } catch (error) {
        console.warn("Failed to update Pendo context:", error);
      }
    }
  };

  // Add Pendo script using useHead
  useHead({
    script: [
      {
        src: `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`,
        async: true,
        onload: initializePendo,
      },
    ],
  });

  // Make updatePendoContext available globally for route changes
  return {
    provide: {
      updatePendoContext,
    },
  };
});
